import {
  Product,
  ProductType,
  ProductMeasurementJob,
  ProductMeasurementMaterial,
  ProductLabourCostType,
} from "generated/graphql";
import * as z from "zod";
import {
  baseProductSchemaSubcontracted,
  requiresPlant,
  baseProductSchema,
  productTypeValidationMap,
  materialsNoPlantRequired,
  filterFilledMaterials,
} from "./ProductCreate";
import {
  requiredNumberField,
  requiredSelectValue,
  optionalPartialAttributes,
  filterSyncArray,
  optionalProductionRateType,
  optionalExternalProductName,
} from "./sharedValidation";

const productionRateTypeRequired = z.object({ productionRateType: z.boolean().optional() });

const plantProductsRequiredUpdate = z.object({
  plantProducts: z.object({
    sync: z
      .array(
        z.object({
          plantId: z.string().min(1, "Plant is required"),
          id: z.string().min(1, "Plant Product is required"),
          applicationRate: z.coerce
            .number({ invalid_type_error: "Depth is required" })
            .min(0.01, "Depth is required"),
        })
      )
      .min(1, "At least one plant and plant product must be selected"),
  }),
});

const plantProductsRequiredUpdateAllowZeroDepth = z.object({
  plantProducts: z.object({
    sync: z
      .array(
        z.object({
          plantId: z.string().min(1, "Plant is required"),
          id: z.string().min(1, "Plant Product is required"),
          applicationRate: z.coerce
            .number({ invalid_type_error: "Depth is required" })
            .min(0, "Depth is required"),
        })
      )
      .min(1, "At least one plant and plant product must be selected"),
  }),
});

const materialsNoPlantRequiredUpdate = z.object({
  materialsNoPlant: z.array(
    z
      .object({
        id: z.string().nullable().optional(),
        name: z.string(),
        percentage: z.union([
          z.string().length(0),
          z.coerce
            .number({ invalid_type_error: "Percentage is required" })
            .min(0, "Percentage must be 0 or greater")
            .max(100, "Percentage cannot exceed 100"),
        ]),
        rate: z.union([
          z.string().length(0),
          z.coerce.number().min(0.01, "Rate must be greater than 0"),
        ]),
      })
      .superRefine((material, ctx) => {
        const hasRateOrPercentage = material.percentage !== "" || material.rate !== "";
        const hasNameOrPercentage = material.name.trim() !== "" || material.percentage !== "";

        if (hasRateOrPercentage && material.name.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Material name is required",
            path: ["name"],
          });
        }

        if (hasNameOrPercentage && material.rate === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Rate is required",
            path: ["rate"],
          });
        }
      })
  ),
});

const materialsNoPlantOptionalUpdate = z.preprocess(
  filterFilledMaterials,
  z.array(materialsNoPlantRequiredUpdate.shape.materialsNoPlant.element).optional()
);

const baseSchemaMapUpdate = {
  [ProductType.ASPHALT]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.ASPHALT])
    .merge(productionRateTypeRequired),
  [ProductType.ASPHALT_CURBING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.ASPHALT_CURBING])
    .merge(productionRateTypeRequired),
  [ProductType.CONCRETE_CURB_OR_GUTTER]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.CONCRETE_CURB_OR_GUTTER]
  ),
  [ProductType.CONCRETE_FLATWORK]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.CONCRETE_FLATWORK]
  ),
  [ProductType.CONCRETE_WATERWAY]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.CONCRETE_WATERWAY]
  ),
  [ProductType.CONCRETE_FOOTING]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.CONCRETE_FOOTING]
  ),
  [ProductType.CONCRETE_WALL]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.CONCRETE_WALL]
  ),
  [ProductType.CONCRETE_COLUMN]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.CONCRETE_COLUMN]
  ),
  [ProductType.CRACK_SEAL]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.CRACK_SEAL])
    .merge(materialsNoPlantRequiredUpdate),
  [ProductType.CURB_WALL]: baseProductSchema.merge(productTypeValidationMap[ProductType.CURB_WALL]),
  [ProductType.EXCAVATE_OUT]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.EXCAVATE_OUT])
    .merge(productionRateTypeRequired),
  [ProductType.GRADING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.GRADING])
    .merge(productionRateTypeRequired),
  [ProductType.MASTIC]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.MASTIC])
    .merge(materialsNoPlantRequiredUpdate),
  [ProductType.MICROSURFACING]: baseProductSchema.merge(
    productTypeValidationMap[ProductType.MICROSURFACING]
  ),
  [ProductType.SEALCOAT]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.SEALCOAT])
    .merge(materialsNoPlantRequiredUpdate),
  [ProductType.SLURRY_SEAL]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.SLURRY_SEAL])
    .merge(productionRateTypeRequired),
  [ProductType.STRIPING]: baseProductSchema
    .merge(productTypeValidationMap[ProductType.STRIPING])
    .merge(materialsNoPlantRequiredUpdate),
  [ProductType.GENERIC]: baseProductSchema
    .merge(
      z.object({
        areaCoverage: z
          .number({ invalid_type_error: "Area Coverage is required" })
          .positive("Area Coverage should be more than zero"),
      })
    )
    .merge(materialsNoPlantRequiredUpdate),
};

export const getProductUpdateSchemaSubcontracted = z.object({
  id: z.string().min(1, "Product ID is required"),
  name: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  externalProductName: z.string().optional(),
  indirectPercentage: z.number({ invalid_type_error: "Indirect Percentage is required" }).min(0),
  overheadPercentage: z.number({ invalid_type_error: "Overhead Percentage is required" }).min(0),
  markupPercentage: z.number({ invalid_type_error: "Markup Percentage is required" }).min(0),
  statementOfWork: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  subcontracted: z.boolean(),
  measurementJob: z.nativeEnum(ProductMeasurementJob),
});
const baseProductSchemaUnitPriceUpdate = z.object({
  id: z.string().min(1, "Product ID is required"),
  name: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  externalProductName: optionalExternalProductName,
  indirectPercentage: z.number({ invalid_type_error: "Indirect Percentage is required" }).min(0),
  overheadPercentage: z.number({ invalid_type_error: "Overhead Percentage is required" }).min(0),
  markupPercentage: z.number({ invalid_type_error: "Markup Percentage is required" }).min(0),
  statementOfWork: z.string({ invalid_type_error: "Required" }).min(1, "Required"),
  unitPriceProduct: z.boolean(),
  unitPrice: requiredNumberField(
    z
      .number({
        required_error: "Please enter a Number",
        invalid_type_error: "Please enter a Number",
      })
      .min(0)
  ),
  measurementMaterial: z.preprocess(
    (v) => (v === "" ? undefined : v),
    z.nativeEnum(ProductMeasurementMaterial, { invalid_type_error: "Required" }).nullish()
  ),
  labourCostType: z.preprocess(
    (v) => (v === "" ? undefined : v),
    z.nativeEnum(ProductLabourCostType).nullish()
  ),
  dailyUnitsComplete: requiredNumberField(
    z
      .number({
        required_error: "Please enter a Number",
        invalid_type_error: "Please enter a Number",
      })
      .positive()
  ),
  measurementJob: z.nativeEnum(ProductMeasurementJob),
  additionalCosts: z.preprocess(
    (val) => ({
      sync: filterSyncArray(val, (item) => Boolean(item.id)),
    }),
    z.object({
      sync: z.array(
        z.object({
          id: requiredSelectValue,
          name: z.string(),
        })
      ),
    })
  ),
  crews: z.preprocess(
    (val) => {
      const sync = filterSyncArray(val, (item) => Boolean(item.id));
      return sync.length > 0 ? { sync } : undefined;
    },
    z
      .object({
        sync: z.array(
          z.object({
            id: z.string().min(1, "Crew is required"),
            quantity: z
              .number({ invalid_type_error: "Quantity is required" })
              .int("Quantity must be a whole number")
              .positive("Quantity must be greater than 0"),
          })
        ),
      })
      .optional()
  ),
  productionRateType: optionalProductionRateType,
  materialsNoPlant: materialsNoPlantOptionalUpdate,
});

export const getProductUpdateSchemaUnitPrice = ({ productType }: { productType: ProductType }) => {
  const typeValidation = productTypeValidationMap[productType];
  let schema = baseProductSchemaUnitPriceUpdate as any;
  if (typeValidation) {
    schema = schema.merge(
      z.object({
        attributes: optionalPartialAttributes(typeValidation.shape.attributes),
      })
    );
  }
  // Plant products are optional for unit-priced products
  if (requiresPlant.includes(productType)) {
    schema = schema.merge(
      z.object({
        plantProducts: z.preprocess(
          (val) => {
            const sync = filterSyncArray(val, (item) => Boolean(item.plantId && item.id));
            return sync.length > 0 ? { sync } : undefined;
          },
          z
            .object({
              sync: z.array(
                z.object({
                  plantId: z.string().min(1, "Plant is required"),
                  id: z.string().min(1, "Plant Product is required"),
                  applicationRate: z.coerce
                    .number({ invalid_type_error: "Depth is required" })
                    .min(0, "Depth is required"),
                })
              ),
            })
            .optional()
        ),
      })
    );
  }
  return schema;
};

export const getProductUpdateSchema = ({ productType }: { productType: ProductType }) => {
  const baseUpdateSchema = z.object({
    id: z.string().min(1, "Product ID is required"),
    crews: z.object({
      sync: z
        .array(
          z.object({
            id: z.string().min(1, "Crew is required"),
            quantity: z
              .number({ invalid_type_error: "Quantity is required" })
              .int("Quantity must be a whole number")
              .positive("Quantity must be greater than 0"),
          })
        )
        .min(1, "At least one crew is required"),
    }),
    equipmentPieces: z
      .object({
        sync: z.array(
          z.object({
            id: requiredSelectValue,
            quantity: requiredNumberField(
              z
                .number({
                  required_error: "Please enter a Number",
                  invalid_type_error: "Please enter a Number",
                })
                .min(0, "Quantity must be 0 or greater")
            ),
            name: z.string(),
          })
        ),
      })
      .optional(),
    additionalCosts: z.object({
      sync: z.array(
        z.object({
          id: requiredSelectValue,
          name: z.string(),
          // type: z.nativeEnum(ProductAdditionalCostType),
        })
      ),
    }),
  });

  const schemaWithPlantProducts = requiresPlant.includes(productType)
    ? baseUpdateSchema.merge(
        [ProductType.ASPHALT, ProductType.EXCAVATE_OUT, ProductType.GRADING].includes(productType)
          ? plantProductsRequiredUpdateAllowZeroDepth
          : plantProductsRequiredUpdate
      )
    : baseUpdateSchema;

  return baseSchemaMapUpdate[productType].merge(schemaWithPlantProducts);
};

export const defaultValues = {};

export const getDefaultValues = (initialDefaultValues) => {
  const calculatedValues = {
    crews: {
      sync:
        initialDefaultValues?.productCrews?.map((productCrew) => ({
          id: productCrew.crew.id,
          quantity: productCrew.quantity,
        })) ?? [],
    },
    plantProducts: {
      sync:
        initialDefaultValues?.plantProducts?.map((plantProduct) => ({
          plantId: plantProduct.plantId,
          id: plantProduct.id,
          applicationRate: plantProduct.pivot?.applicationRate,
        })) || [],
    },
    equipmentPieces: {
      sync: initialDefaultValues?.productEquipmentPieces?.map((productEquipmentPiece) => ({
        id: productEquipmentPiece.equipmentPiece.id,
        quantity: productEquipmentPiece.quantity,
        name: productEquipmentPiece.equipmentPiece.name,
      })),
    },
    additionalCosts: {
      sync: initialDefaultValues?.productAdditionalCosts?.map((productAdditionalCost) => ({
        id: productAdditionalCost.additionalCost.id,
        type: productAdditionalCost.type,
        name: productAdditionalCost.additionalCost.name,
      })),
    },
  };

  return {
    ...defaultValues,
    ...initialDefaultValues,
    attributes: initialDefaultValues?.attributes ?? {},
    ...calculatedValues,
  };
};
